import {
  type ComparisonOperator,
  type PreviewVariable,
  PreviewVariableCollector,
  compareValues,
  isNumeric,
  lookupValue,
  type NameMatching,
  mirrorOperator,
  toNumber,
  toText,
  type PersonalizationPrimitive,
  type RenderErrorHandler,
  truthy,
  unescapeSampleValue,
} from "./preview-variables";

// A preview interpreter for the AMPscript subset marketing emails use: SET,
// IF/ELSEIF/ELSE, inline output, personalization strings, data extension
// lookups, and the common string, number and date functions. It never throws:
// anything it can't parse or run renders as empty, so one exotic call never
// blanks the rest of the email the way a strict parse would.

type Values = Record<string, PersonalizationPrimitive>;

const AMPSCRIPT_NAMES: NameMatching = { ignoreCase: true };

type Token =
  | { type: "string"; value: string }
  | { type: "number"; value: number }
  | { type: "var"; value: string }
  | { type: "attr"; value: string }
  | { type: "ident"; value: string }
  | { type: "op"; value: string }
  | { type: "(" | ")" | "," };

type Expr =
  | { kind: "literal"; value: PersonalizationPrimitive }
  | { kind: "var"; name: string }
  | { kind: "attr"; name: string }
  | { kind: "call"; name: string; args: Expr[] }
  | { kind: "binary"; op: string; left: Expr; right: Expr }
  | { kind: "not"; operand: Expr };

type Instruction =
  | { kind: "text"; text: string }
  | { kind: "output"; expr: Expr }
  | { kind: "set"; name: string; expr: Expr }
  // A null condition or loop did not parse; its block stays closed so nesting balances.
  | { kind: "if" | "elseif"; condition: Expr | null }
  | { kind: "else" | "endif" | "next" }
  | { kind: "for"; name: string; from: Expr }
  | { kind: "for"; name: null; from: null }
  | { kind: "call"; expr: Expr };

type Raw =
  | PersonalizationPrimitive
  | null
  | { rowset: string }
  | { row: string };

interface Value {
  raw: Raw;
  /** Every attribute this value was derived from. */
  sources: Set<string>;
  /** The attribute this value passes through unchanged, if any. */
  direct?: string;
}

const KEYWORDS = new Set([
  "var",
  "set",
  "if",
  "then",
  "elseif",
  "else",
  "endif",
  "for",
  "to",
  "downto",
  "do",
  "next",
  "and",
  "or",
  "not",
  "true",
  "false",
]);

const COMPARISON_OPERATORS: Record<string, ComparisonOperator> = {
  "==": "==",
  "=": "==",
  "!=": "!=",
  "<>": "!=",
  ">": ">",
  "<": "<",
  ">=": ">=",
  "<=": "<=",
};

const SEGMENT_RE =
  /%%\[([\s\S]*?)\]%%|%%=([\s\S]*?)=%%|%%([A-Za-z_][A-Za-z0-9_ -]{0,100})%%/g;

/** True when the template has AMPscript blocks, inline output or personalization strings. */
export function containsAmpscript(template: string): boolean {
  return new RegExp(SEGMENT_RE.source).test(template);
}

export function renderAmpscript(
  template: string,
  values: Values,
  onError?: RenderErrorHandler,
): string {
  return new Interpreter("render", values, undefined, onError).run(
    parseTemplate(template),
  );
}

export function extractAmpscriptVariables(template: string): PreviewVariable[] {
  const collector = new PreviewVariableCollector(AMPSCRIPT_NAMES);
  new Interpreter("trace", {}, collector).run(parseTemplate(template));
  return collector.list();
}

function parseTemplate(template: string): Instruction[] {
  const instructions: Instruction[] = [];
  let last = 0;
  for (const match of template.matchAll(SEGMENT_RE)) {
    const index = match.index ?? 0;
    if (index > last) {
      instructions.push({ kind: "text", text: template.slice(last, index) });
    }
    const [, block, inline, personalization] = match;
    if (block !== undefined) {
      instructions.push(...new Parser(lex(block)).statements());
    } else if (inline !== undefined) {
      const expr = new Parser(lex(inline)).expressionOrNull();
      if (expr) instructions.push({ kind: "output", expr });
    } else if (personalization !== undefined) {
      instructions.push({
        kind: "output",
        expr: { kind: "attr", name: personalization.trim() },
      });
    }
    last = index + match[0].length;
  }
  if (last < template.length) {
    instructions.push({ kind: "text", text: template.slice(last) });
  }
  return instructions;
}

function lex(code: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < code.length) {
    const char = code[i] ?? "";
    if (/\s/.test(char)) {
      i++;
    } else if (code.startsWith("/*", i)) {
      const end = code.indexOf("*/", i + 2);
      i = end === -1 ? code.length : end + 2;
    } else if (char === '"' || char === "'") {
      let value = "";
      i++;
      while (i < code.length) {
        if (code[i] === char) {
          // A doubled quote is AMPscript's escape for a literal quote.
          if (code[i + 1] === char) {
            value += char;
            i += 2;
            continue;
          }
          i++;
          break;
        }
        value += code[i];
        i++;
      }
      tokens.push({ type: "string", value });
    } else if (/[0-9]/.test(char)) {
      const match = /^\d+(?:\.\d+)?/.exec(code.slice(i));
      const text = match?.[0] ?? char;
      tokens.push({ type: "number", value: Number(text) });
      i += text.length;
    } else if (char === "@") {
      const match = /^@[A-Za-z0-9_]+/.exec(code.slice(i));
      const text = match?.[0] ?? "@";
      tokens.push({ type: "var", value: text.slice(1) });
      i += text.length;
    } else if (char === "[") {
      const end = code.indexOf("]", i + 1);
      const name = code.slice(i + 1, end === -1 ? code.length : end);
      tokens.push({ type: "attr", value: name.trim() });
      i = end === -1 ? code.length : end + 1;
    } else if (/[A-Za-z_]/.test(char)) {
      const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(code.slice(i));
      const text = match?.[0] ?? char;
      tokens.push({ type: "ident", value: text });
      i += text.length;
    } else if (char === "(" || char === ")" || char === ",") {
      tokens.push({ type: char });
      i++;
    } else {
      const op = ["==", "!=", "<>", ">=", "<="].find((candidate) =>
        code.startsWith(candidate, i),
      );
      const value = op ?? char;
      tokens.push({ type: "op", value });
      i += value.length;
    }
  }
  return tokens;
}

class ParseError extends Error {}

function unparsedBlockOpener(token: Token | undefined): Instruction | null {
  if (token?.type !== "ident") return null;
  const keyword = token.value.toLowerCase();
  if (keyword === "if" || keyword === "elseif") {
    return { kind: keyword, condition: null };
  }
  return keyword === "for" ? { kind: "for", name: null, from: null } : null;
}

class Parser {
  private pos = 0;

  constructor(private readonly tokens: Token[]) {}

  statements(): Instruction[] {
    const instructions: Instruction[] = [];
    while (this.pos < this.tokens.length) {
      const start = this.pos;
      try {
        const instruction = this.statement();
        if (instruction) instructions.push(instruction);
      } catch (error) {
        if (!(error instanceof ParseError)) throw error;
        const opener = unparsedBlockOpener(this.tokens[start]);
        if (opener) instructions.push(opener);
      }
      // Skip whatever a failed or unrecognized statement left behind.
      if (this.pos === start) this.pos++;
    }
    return instructions;
  }

  expressionOrNull(): Expr | null {
    try {
      return this.expression();
    } catch (error) {
      if (error instanceof ParseError) return null;
      throw error;
    }
  }

  private statement(): Instruction | null {
    const token = this.peek();
    if (token?.type !== "ident") return null;
    const keyword = token.value.toLowerCase();
    switch (keyword) {
      case "var":
        this.pos++;
        while (this.peek()?.type === "var" || this.peek()?.type === ",") {
          this.pos++;
        }
        return null;
      case "set": {
        this.pos++;
        const target = this.peek();
        if (target?.type !== "var") throw new ParseError();
        this.pos++;
        this.expectOp("=");
        return { kind: "set", name: target.value, expr: this.expression() };
      }
      case "if":
      case "elseif": {
        this.pos++;
        const condition = this.expression();
        this.skipKeyword("then");
        return { kind: keyword, condition };
      }
      case "else":
      case "endif":
        this.pos++;
        return { kind: keyword };
      case "for": {
        this.pos++;
        const counter = this.peek();
        if (counter?.type !== "var") throw new ParseError();
        this.pos++;
        this.expectOp("=");
        const from = this.expression();
        if (!this.skipKeyword("to")) this.skipKeyword("downto");
        this.expression();
        this.skipKeyword("do");
        return { kind: "for", name: counter.value, from };
      }
      case "next":
        this.pos++;
        if (this.peek()?.type === "var") this.pos++;
        return { kind: "next" };
      default:
        if (this.tokens[this.pos + 1]?.type === "(") {
          return { kind: "call", expr: this.expression() };
        }
        return null;
    }
  }

  private expression(minPrecedence = 0): Expr {
    let left = this.unary();
    for (;;) {
      const op = this.binaryOperator();
      if (!op || op.precedence <= minPrecedence) return left;
      this.pos++;
      const right = this.expression(op.precedence);
      left = { kind: "binary", op: op.value, left, right };
    }
  }

  private binaryOperator(): { value: string; precedence: number } | null {
    const token = this.peek();
    if (token?.type === "ident") {
      const word = token.value.toLowerCase();
      if (word === "or") return { value: "or", precedence: 1 };
      if (word === "and") return { value: "and", precedence: 2 };
      return null;
    }
    if (token?.type === "op" && token.value in COMPARISON_OPERATORS) {
      return { value: token.value, precedence: 3 };
    }
    return null;
  }

  private unary(): Expr {
    const token = this.peek();
    if (token?.type === "ident" && token.value.toLowerCase() === "not") {
      this.pos++;
      return { kind: "not", operand: this.unary() };
    }
    if (token?.type === "op" && token.value === "-") {
      this.pos++;
      const number = this.peek();
      if (number?.type !== "number") throw new ParseError();
      this.pos++;
      return { kind: "literal", value: -number.value };
    }
    return this.primary();
  }

  private primary(): Expr {
    const token = this.peek();
    if (!token) throw new ParseError();
    this.pos++;
    switch (token.type) {
      case "string":
      case "number":
        return { kind: "literal", value: token.value };
      case "var":
        return { kind: "var", name: token.value };
      case "attr":
        return { kind: "attr", name: token.value };
      case "(": {
        const inner = this.expression();
        if (this.peek()?.type !== ")") throw new ParseError();
        this.pos++;
        return inner;
      }
      case "ident": {
        const word = token.value.toLowerCase();
        if (word === "true" || word === "false") {
          return { kind: "literal", value: word === "true" };
        }
        if (this.peek()?.type === "(") {
          this.pos++;
          const args: Expr[] = [];
          while (this.peek() && this.peek()?.type !== ")") {
            args.push(this.expression());
            if (this.peek()?.type === ",") this.pos++;
            else break;
          }
          if (this.peek()?.type !== ")") throw new ParseError();
          this.pos++;
          return { kind: "call", name: token.value, args };
        }
        if (KEYWORDS.has(word)) throw new ParseError();
        // A bare personalization string, e.g. `SET @name = FirstName`.
        return { kind: "attr", name: token.value };
      }
      default:
        throw new ParseError();
    }
  }

  private expectOp(value: string): void {
    const token = this.peek();
    if (token?.type !== "op" || token.value !== value) throw new ParseError();
    this.pos++;
  }

  private skipKeyword(word: string): boolean {
    const token = this.peek();
    if (token?.type === "ident" && token.value.toLowerCase() === word) {
      this.pos++;
      return true;
    }
    return false;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }
}

interface Frame {
  parentActive: boolean;
  active: boolean;
  taken: boolean;
  isLoop: boolean;
}

class Interpreter {
  private readonly variables = new Map<string, Value>();
  private readonly frames: Frame[] = [];
  private inCondition = false;
  private output = "";

  constructor(
    private readonly mode: "render" | "trace",
    private readonly values: Values,
    private readonly collector?: PreviewVariableCollector,
    private readonly onError?: RenderErrorHandler,
  ) {}

  run(instructions: Instruction[]): string {
    for (const instruction of instructions) this.step(instruction);
    return this.output;
  }

  private get active(): boolean {
    return this.frames.at(-1)?.active ?? true;
  }

  private step(instruction: Instruction): void {
    switch (instruction.kind) {
      case "if": {
        const parentActive = this.active;
        const taken = parentActive && this.condition(instruction.condition);
        this.frames.push({
          parentActive,
          active: this.mode === "trace" ? parentActive : taken,
          taken,
          isLoop: false,
        });
        return;
      }
      case "elseif": {
        const frame = this.frames.at(-1);
        if (!frame) return;
        // Evaluated even after an earlier branch was taken, so tracing reads every branch's variables.
        const holds =
          frame.parentActive && this.condition(instruction.condition);
        const taken = !frame.taken && holds;
        frame.active = this.mode === "trace" ? frame.parentActive : taken;
        frame.taken ||= taken;
        return;
      }
      case "else": {
        const frame = this.frames.at(-1);
        if (!frame) return;
        frame.active =
          this.mode === "trace"
            ? frame.parentActive
            : frame.parentActive && !frame.taken;
        return;
      }
      case "endif":
      case "next":
        this.frames.pop();
        return;
      case "for": {
        // A preview shows one pass of the loop, with the counter at its start.
        const parentActive = this.active;
        if (instruction.name === null) {
          this.frames.push({
            parentActive,
            active: false,
            taken: true,
            isLoop: true,
          });
          return;
        }
        if (parentActive) {
          this.variables.set(
            instruction.name.toLowerCase(),
            this.evaluate(instruction.from),
          );
        }
        this.frames.push({
          parentActive,
          active: parentActive,
          taken: true,
          isLoop: true,
        });
        return;
      }
      default:
        break;
    }
    if (!this.active) return;
    switch (instruction.kind) {
      case "text":
        this.output += instruction.text;
        return;
      case "output":
        this.output += toText(this.evaluate(instruction.expr).raw);
        return;
      case "set":
        this.variables.set(
          instruction.name.toLowerCase(),
          this.evaluate(instruction.expr),
        );
        return;
      case "call": {
        const value = this.evaluate(instruction.expr);
        const name =
          instruction.expr.kind === "call"
            ? instruction.expr.name.toLowerCase()
            : "";
        if (name === "output" || name === "outputline") {
          this.output += toText(value.raw);
        }
        return;
      }
    }
  }

  private condition(expr: Expr | null): boolean {
    if (!expr) return false;
    this.inCondition = true;
    const value = this.evaluate(expr);
    this.inCondition = false;
    for (const source of value.sources) this.collector?.read(source, true);
    return truthy(value.raw);
  }

  private evaluate(expr: Expr): Value {
    switch (expr.kind) {
      case "literal":
        return { raw: expr.value, sources: new Set() };
      case "var":
        return (
          this.variables.get(expr.name.toLowerCase()) ?? {
            raw: null,
            sources: new Set(),
          }
        );
      case "attr":
        return this.attribute(expr.name);
      case "not": {
        const operand = this.evaluate(expr.operand);
        return { raw: !truthy(operand.raw), sources: operand.sources };
      }
      case "binary":
        return this.binary(expr.op, expr.left, expr.right);
      case "call":
        return this.call(
          expr.name.toLowerCase(),
          expr.args.map((arg) => this.evaluate(arg)),
        );
    }
  }

  private attribute(name: string): Value {
    this.collector?.read(name, this.inCondition);
    return {
      raw: lookupValue(this.values, name, AMPSCRIPT_NAMES) ?? "",
      sources: new Set([name]),
      direct: name,
    };
  }

  private binary(op: string, leftExpr: Expr, rightExpr: Expr): Value {
    const left = this.evaluate(leftExpr);
    const right = this.evaluate(rightExpr);
    const sources = new Set([...left.sources, ...right.sources]);
    const lower = op.toLowerCase();
    if (lower === "and") {
      return { raw: truthy(left.raw) && truthy(right.raw), sources };
    }
    if (lower === "or") {
      return { raw: truthy(left.raw) || truthy(right.raw), sources };
    }
    const operator = COMPARISON_OPERATORS[op];
    if (!operator) return { raw: null, sources };
    if (this.inCondition) {
      this.recordComparison(left, right, operator);
      this.recordComparison(right, left, mirrorOperator(operator));
    }
    // Now() and date attributes are text, so compare their instants rather than their spelling.
    const leftDate = parseAmpscriptDate(left.raw);
    const rightDate = parseAmpscriptDate(right.raw);
    if (leftDate !== null && rightDate !== null) {
      return {
        raw: compareValues(leftDate, rightDate, operator, { ignoreCase: true }),
        sources,
      };
    }
    return {
      raw: compareValues(
        unescapeSampleValue(left.raw),
        unescapeSampleValue(right.raw),
        operator,
        { ignoreCase: true },
      ),
      sources,
    };
  }

  private recordComparison(
    subject: Value,
    other: Value,
    operator: ComparisonOperator,
  ): void {
    if (!subject.direct || other.sources.size > 0) return;
    const literal = other.raw;
    if (
      typeof literal === "string" ||
      typeof literal === "number" ||
      typeof literal === "boolean"
    ) {
      this.collector?.compare(subject.direct, operator, literal);
    }
  }

  private call(name: string, args: Value[]): Value {
    const sources = new Set(args.flatMap((arg) => [...arg.sources]));
    const result = (raw: Raw): Value => ({ raw, sources });
    const passThrough = (value: Value | undefined, raw: Raw): Value => ({
      raw,
      sources: value?.sources ?? new Set(),
      direct: value?.direct,
    });
    const [first, second, third] = args;
    const text = (value: Value | undefined) => toText(value?.raw ?? null);
    const number = (value: Value | undefined) => toNumber(value?.raw ?? null);
    switch (name) {
      case "v":
      case "treatascontent":
      case "output":
      case "outputline":
        return first ?? result(null);
      case "attributevalue":
        return this.attribute(text(first));
      case "lookup":
        return this.attribute(`${text(first)}.${text(second)}`);
      case "lookuprows":
      case "lookuporderedrows":
      case "lookuprowscs":
      case "lookuporderedrowscs":
        return result({ rowset: text(first) });
      case "rowcount":
        return result(
          first?.raw && typeof first.raw === "object" && "rowset" in first.raw
            ? 1
            : 0,
        );
      case "row": {
        const rowset = first?.raw;
        return result(
          rowset && typeof rowset === "object" && "rowset" in rowset
            ? { row: rowset.rowset }
            : null,
        );
      }
      case "field": {
        const row = first?.raw;
        const table =
          row && typeof row === "object" && "row" in row ? row.row : "";
        return this.attribute(
          table ? `${table}.${text(second)}` : text(second),
        );
      }
      case "concat":
        return result(args.map((arg) => toText(arg.raw)).join(""));
      case "iif":
        if (this.mode === "trace") {
          for (const source of first?.sources ?? []) {
            this.collector?.read(source, true);
          }
        }
        return truthy(first?.raw ?? null)
          ? (second ?? result(null))
          : (third ?? result(null));
      case "empty":
      case "isnull":
        return result(first?.raw == null || toText(first.raw) === "");
      case "length":
      case "len":
        return result(text(first).length);
      case "uppercase":
        return passThrough(first, text(first).toUpperCase());
      case "lowercase":
        return passThrough(first, text(first).toLowerCase());
      case "propercase":
        return passThrough(
          first,
          text(first)
            .toLowerCase()
            .replace(/\b\w/g, (letter) => letter.toUpperCase()),
        );
      case "trim":
        return passThrough(first, text(first).trim());
      case "substring": {
        const start = Math.max(number(second) - 1, 0);
        const length = third ? number(third) : undefined;
        return result(
          text(first).slice(
            start,
            length === undefined ? undefined : start + length,
          ),
        );
      }
      case "replace":
        return result(text(first).split(text(second)).join(text(third)));
      case "indexof":
        return result(text(first).indexOf(text(second)) + 1);
      case "add":
        return result(number(first) + number(second));
      case "subtract":
        return result(number(first) - number(second));
      case "multiply":
        return result(number(first) * number(second));
      case "divide":
        return result(
          number(second) === 0 ? 0 : number(first) / number(second),
        );
      case "mod":
        return result(
          number(second) === 0 ? 0 : number(first) % number(second),
        );
      case "formatnumber":
      case "formatcurrency":
      case "format":
        return result(formatValue(first?.raw ?? null, text(second), name));
      case "now": {
        // A preview can pin "now" (for example to the send time) with a `now` value.
        const pinned = this.attribute("now");
        const instant =
          pinned.raw === "" ? Date.now() : parseAmpscriptDate(pinned.raw);
        return {
          raw:
            instant === null
              ? toText(pinned.raw)
              : formatAmpscriptDate(instant),
          sources: pinned.sources,
        };
      }
      case "systemdatetolocaldate":
        return first ?? result(null);
      case "dateparse": {
        const instant = parseAmpscriptDate(first?.raw ?? null);
        return result(
          instant === null ? text(first) : formatAmpscriptDate(instant),
        );
      }
      case "dateadd": {
        const instant = parseAmpscriptDate(first?.raw ?? null);
        if (instant === null) return result(text(first));
        return result(
          formatAmpscriptDate(addToDate(instant, number(second), text(third))),
        );
      }
      case "datediff": {
        const start = parseAmpscriptDate(first?.raw ?? null);
        const end = parseAmpscriptDate(second?.raw ?? null);
        if (start === null || end === null) return result("");
        return result(dateDifference(start, end, text(third)));
      }
      case "formatdate":
        return result(text(first));
      case "redirectto":
        return result(text(first));
      case "cloudpagesurl":
      case "micrositeurl":
        return result("#");
      case "guid":
        return result("00000000-0000-0000-0000-000000000000");
      default:
        // Unsupported calls (content blocks, HTTP, data writes) render as empty.
        this.onError?.(`Unsupported preview function: ${name}`);
        return result("");
    }
  }
}

function formatValue(raw: Raw, format: string, fn: string): string {
  if (!isNumeric(raw)) return toText(raw);
  const value = toNumber(raw);
  const upper = format.toUpperCase();
  // toLocaleString throws outside 0-100; 20 is plenty for a preview.
  const decimals = Math.min(
    20,
    Number(/\d+/.exec(upper)?.[0] ?? (upper.startsWith("C") ? 2 : 0)),
  );
  if (fn === "formatcurrency" || upper.startsWith("C")) {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  if (upper.startsWith("N")) {
    return value.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return toText(raw);
}

const US_DATE_RE =
  /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AP]M)?)?$/i;
const ISO_DATE_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?\s*(?:Z|UTC)?$/i;

/** The UTC instant of an AMPscript date string (`Now()` output, ISO, or M/D/YYYY), or null. */
function parseAmpscriptDate(raw: Raw): number | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  const us = US_DATE_RE.exec(value);
  if (us) {
    const [
      ,
      month,
      day,
      year,
      hour = "0",
      minute = "0",
      second = "0",
      meridiem,
    ] = us;
    let hours = Number(hour) % (meridiem ? 12 : 24);
    if (meridiem?.toUpperCase() === "PM") hours += 12;
    return Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      hours,
      Number(minute),
      Number(second),
    );
  }
  const iso = ISO_DATE_RE.exec(value);
  if (iso) {
    const [, year, month, day, hour = "0", minute = "0", second = "0"] = iso;
    return Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    );
  }
  return null;
}

function formatAmpscriptDate(instant: number): string {
  return new Date(instant).toLocaleString("en-US", { timeZone: "UTC" });
}

const DAY_MS = 86_400_000;
// AMPscript's date parts: Y and M are calendar steps, the rest fixed lengths.
const MILLISECONDS_PER: Record<string, number> = {
  D: DAY_MS,
  H: 3_600_000,
  MI: 60_000,
};

function addToDate(instant: number, amount: number, part: string): number {
  const date = new Date(instant);
  switch (part.toUpperCase()) {
    case "Y":
      date.setUTCFullYear(date.getUTCFullYear() + amount);
      return date.getTime();
    case "M":
      date.setUTCMonth(date.getUTCMonth() + amount);
      return date.getTime();
    default:
      return (
        instant + amount * (MILLISECONDS_PER[part.toUpperCase()] ?? DAY_MS)
      );
  }
}

function dateDifference(start: number, end: number, part: string): number {
  const from = new Date(start);
  const to = new Date(end);
  const months =
    (to.getUTCFullYear() - from.getUTCFullYear()) * 12 +
    (to.getUTCMonth() - from.getUTCMonth());
  switch (part.toUpperCase()) {
    case "Y":
      return Math.trunc(months / 12);
    case "M":
      return months;
    default:
      return Math.trunc(
        (end - start) / (MILLISECONDS_PER[part.toUpperCase()] ?? DAY_MS),
      );
  }
}
